import React, { useCallback } from 'react';
import { css } from '@emotion/react';
import copy from 'copy-to-clipboard';
import { FacebookLogo } from '@core/assets/FacebookLogo';
import { Link } from '@core/assets/Link';
import { Message } from '@core/assets/Message';
import { More } from '@core/assets/More';
import { TwitterX } from '@core/assets/TwitterX';
import { WhatsappLogo } from '@core/assets/WhatsappLogo';
import { useContextTranslation } from '@core/i18n';
import { sendPixelTrackingWithSession } from '@core/tracking';
import { isIOS, isMobileWeb } from '@core/userAgent';
import UnstyledLink from '@embed/components/common/UnstyledLink';
import { SharePanelType } from '@embed/components/VideoPlayer/Player3/components/modals/shared/SharePanel/types';
import { EventType, useCustomPlayerVideoEvent, } from '@embed/components/VideoPlayer/Player3/hooks/useCustomPlayerVideoEvent';
import { useGetPlayerNestedTrackingPayload } from '@embed/components/VideoPlayer/Player3/hooks/useGetPlayerNestedTrackingPayload';
import { shallow, useStore } from '@embed/components/VideoPlayer/Player3/store';
import { COPY_BUTTON, COPY_BUTTON_IMG, COPY_BUTTON_LABEL, } from '@player/testIds';
import { PLAYER_SHARE_VIDEO } from '@player/tracking/endpoints';
import { CopyButton, SharePanelOption, SharePanelOptionContainer, SharePanelOptionLabel, } from './SharePanelButton.styles';
/**
 *
 */
export const SharePanelButton = ({ type, onDismiss, onLinkCopy, shareMessage, shareUrl, videoEl, shareSeconds, }) => {
    const { t } = useContextTranslation();
    const params = useStore(({ params }) => params);
    const video = useStore(({ video }) => video);
    const getPlayerNestedTrackingPayload = useGetPlayerNestedTrackingPayload();
    const liveStreamActions = useStore(useCallback((state) => video?.live_stream_id
        ? state.liveStreamActions[video?.live_stream_id]
        : undefined, [video?.live_stream_id]), shallow);
    const message = shareMessage ?? video?.caption;
    const encodedMsg = encodeURIComponent(message ?? '');
    const encodedURL = encodeURIComponent(shareUrl ?? '');
    const actionHref = useCallback(() => {
        switch (type) {
            case SharePanelType.COPY:
            case SharePanelType.NATIVE:
                return '#';
            case SharePanelType.SMS:
                return `${isIOS ? 'sms:&' : 'sms:?'}&body=${`${encodedMsg} ${encodedURL}`}`;
            case SharePanelType.WHATSAPP:
                return `https://wa.me/?text=${`${encodedMsg} ${encodedURL}`}`;
            case SharePanelType.FACEBOOK:
                return `https://www.facebook.com/sharer/sharer.php?u=${encodedURL}&t=${encodedMsg}&src=sdkpreparse`;
            case SharePanelType.TWITTER:
                return `https://twitter.com/intent/tweet?text=${encodedMsg}&url=${encodedURL}`;
        }
    }, [encodedMsg, encodedURL, type]);
    const trackSharing = useCallback(({ platform, video, shareSeconds, }) => {
        sendPixelTrackingWithSession({
            endpoint: PLAYER_SHARE_VIDEO,
            data: {
                event_properties: {
                    ...getPlayerNestedTrackingPayload(),
                    _video_id: video?.encoded_id,
                    elapsed_time: videoEl.elapsedTime,
                    variant: video?.variant,
                    share_target: platform,
                    _live_stream_id: video?.live_stream_id,
                    live_stream_status: video?.live_stream_status,
                    share_seconds: shareSeconds,
                },
            },
            host: params?.api_host_tracking,
        });
    }, [
        params?.api_host_tracking,
        getPlayerNestedTrackingPayload,
        videoEl.elapsedTime,
    ]);
    const customPlayerVideoEvent = useCustomPlayerVideoEvent();
    const onClick = useCallback((clickType) => () => {
        trackSharing({
            params,
            platform: clickType,
            video,
            shareSeconds,
        });
        liveStreamActions?.trackShare();
        customPlayerVideoEvent({
            type: EventType.FW_VIDEO_SHARE,
            data: {
                share_type: clickType,
            },
        });
        onDismiss?.();
    }, [
        customPlayerVideoEvent,
        liveStreamActions,
        onDismiss,
        params,
        trackSharing,
        video,
        shareSeconds,
    ]);
    const onCopyClick = useCallback((event) => {
        event.preventDefault();
        copy(shareUrl ?? '', {
            format: 'text/plain',
        });
        onClick('copy_link')();
        onLinkCopy();
    }, [onClick, onLinkCopy, shareUrl]);
    const onMoreClick = useCallback(async (event) => {
        event.preventDefault();
        try {
            await navigator.share({
                text: video?.caption,
                title: '',
                url: shareUrl,
            });
            customPlayerVideoEvent({
                type: EventType.FW_VIDEO_SHARE,
                data: {
                    share_type: 'web-share',
                },
            });
        }
        catch {
            return null;
        }
        onClick(isMobileWeb() ? 'mobile_native' : 'web_native')();
    }, [video?.caption, shareUrl, onClick, customPlayerVideoEvent]);
    const onClickAction = useCallback((e) => {
        switch (type) {
            case SharePanelType.COPY:
                return onCopyClick(e);
            case SharePanelType.NATIVE:
                return onMoreClick(e);
            case SharePanelType.SMS:
                return onClick(SharePanelType.SMS)();
            case SharePanelType.WHATSAPP:
                return onClick(SharePanelType.WHATSAPP)();
            case SharePanelType.FACEBOOK:
                return onClick(SharePanelType.FACEBOOK)();
            case SharePanelType.TWITTER:
                return onClick(SharePanelType.TWITTER)();
        }
    }, [onClick, onCopyClick, onMoreClick, type]);
    const content = (<SharePanelOptionContainer>
      {type === SharePanelType.COPY && (<SharePanelOption className={COPY_BUTTON} data-cy-value={shareUrl} aria-label={t('copy share link')}>
          <Link css={css `
              width: 35px;
              height: 35px;
            `} label="" aria-hidden data-cy-value={shareUrl} data-testid={COPY_BUTTON_IMG}/>
          <SharePanelOptionLabel data-testid={COPY_BUTTON_LABEL} className="share-panel-option-label">
            {t('Copy Link')}
          </SharePanelOptionLabel>
        </SharePanelOption>)}
      {type === SharePanelType.SMS && (<SharePanelOption aria-label={t('message share link')}>
          <Message css={css `
              width: 35px;
              height: 35px;
            `} label="" aria-hidden data-cy-value={shareUrl} data-testid={COPY_BUTTON_IMG}/>
          <SharePanelOptionLabel data-testid={COPY_BUTTON_LABEL} className="share-panel-option-label">
            {t('Message')}
          </SharePanelOptionLabel>
        </SharePanelOption>)}
      {type === SharePanelType.WHATSAPP && (<SharePanelOption aria-label={t('share on WhatsApp')}>
          <WhatsappLogo css={css `
              width: 35px;
              height: 35px;
            `} label="" aria-hidden data-cy-value={shareUrl} data-testid={COPY_BUTTON_IMG}/>
          {/* eslint-disable-next-line zeffo/no-untranslated-labels */}
          <SharePanelOptionLabel data-testid={COPY_BUTTON_LABEL} className="share-panel-option-label">
            WhatsApp
          </SharePanelOptionLabel>
        </SharePanelOption>)}
      {type === SharePanelType.FACEBOOK && (<SharePanelOption aria-label={t('share on Facebook')}>
          <FacebookLogo css={css `
              width: 35px;
              height: 35px;
            `} label="" aria-hidden data-cy-value={shareUrl} data-testid={COPY_BUTTON_IMG}/>
          {/* eslint-disable-next-line zeffo/no-untranslated-labels */}
          <SharePanelOptionLabel data-testid={COPY_BUTTON_LABEL} className="share-panel-option-label">
            Facebook
          </SharePanelOptionLabel>
        </SharePanelOption>)}
      {type === SharePanelType.TWITTER && (<SharePanelOption aria-label={t('share on X')}>
          <TwitterX css={css `
              width: 35px;
              height: 35px;
            `} label="" aria-hidden data-cy-value={shareUrl} data-testid={COPY_BUTTON_IMG}/>
          {/* eslint-disable-next-line zeffo/no-untranslated-labels */}
          <SharePanelOptionLabel data-testid={COPY_BUTTON_LABEL} className="share-panel-option-label">
            X
          </SharePanelOptionLabel>
        </SharePanelOption>)}
      {type === SharePanelType.NATIVE && (<SharePanelOption aria-label={t('more share options')}>
          <More css={css `
              width: 35px;
              height: 35px;
            `} label="" aria-hidden data-cy-value={shareUrl} data-testid={COPY_BUTTON_IMG}/>
          <SharePanelOptionLabel data-testid={COPY_BUTTON_LABEL} className="share-panel-option-label">
            {t('More')}
          </SharePanelOptionLabel>
        </SharePanelOption>)}
    </SharePanelOptionContainer>);
    return type === SharePanelType.COPY ? (<CopyButton onClick={onClickAction} data-testid={COPY_BUTTON}>
      {content}
    </CopyButton>) : (<UnstyledLink target="_blank" href={actionHref()} onClick={onClickAction} rel="noreferrer noopener">
      {content}
    </UnstyledLink>);
};
